<template>
  <modal-wrapper
    :name="modalName"
    :visible="visible"
    classes="withdraw-to-card"
    :before-open="beforeOpen"
    :before-close="beforeClose"
    @close="emit('close')"
  >
    <template v-if="step === 'main'" #header>
      <operation-header
        :plain-text="t('WithdrawToCard | modalHeaderPlainText')"
        :link-text="t('WithdrawToCard | modalHeaderLinkText')"
        :popover-title="t('WithdrawToCard | modalHeaderTitle')"
        :links="headerPopupItems"
      />
    </template>

    <template v-else-if="step === 'cards'" #header>
      <operation-header
        :plain-text="t('WithdrawToCard | cardsModalTitle')"
        header-type="secondary"
        @goBack="backToMainStep"
      />
    </template>

    <template v-else-if="step === 'confirmation'" #header>
      <operation-header
        :plain-text="t('WithdrawToCard | confirmationModalTitle')"
        header-type="secondary"
        @goBack="backToMainStep"
      />
    </template>

    <template v-if="step === 'main'" #body>
      <main
        :coin-id="coinId"
        :coin-amount="coinAmount"
        :fiat-id="fiatId"
        :fiat-amount="fiatAmount"
        :payment-method-id="paymentMethodId"
        :card-number="cardNumber"
        :bank-id="bankId"
        :phone-number="phoneNumber"
        :provider-id="providerId"
        :is-checked-terms-of-use="isCheckedTermsOfUse"
        :error-code="errorCode"
        :is-initialized="isInitialized"
        :is-loading-rate="isLoadingRate"
        :rate-time-to-live="rateTimeToLive"
        :rate="rate"
        :is-withdraw-available="isWithdrawAvailable"
        :available-coins="availableCoins"
        :selected-coin-balance="selectedCoinBalance"
        :coin-amount-validation-rules="coinAmountValidationRules"
        :coin-amount-max-limit="coinAmountMaxLimit"
        :available-fiats="availableFiats"
        :fiat-amount-validation-rules="fiatAmountValidationRules"
        :selected-fiat="selectedFiat"
        :available-payment-methods="availablePaymentMethods"
        :available-banks="availableBanks"
        :available-providers="availableProviders"
        :selected-coin="selectedCoin"
        :active-coin-limits="activeCoinLimits"
        @change-coin-amount="handleChangeCoinAmount"
        @change-coin="handleChangeCoin"
        @change-fiat-amount="handleChangeFiatAmount"
        @change-fiat="handleChangeFiat"
        @chip-selected="handleChipClick"
        @change-payment-method="handleChangePaymentMethod"
        @change-bank="handleBankSelection"
        @change-card-number="handleChangeCardNumber"
        @change-provider="handleChangeProvider"
        @change-phone-number="handleChangePhoneNumber"
        @check-terms-of-use="handleCheckTermsOfUse"
        @focus="handleInputFocus"
        @submit-form="onSubmit"
        @clear-error="errorCode = null"
      />
    </template>

    <template v-else-if="step === 'cards'" #body>
      <select-card
        :cards="cards"
        @click-on-card="handleSelectCard"
        @add-new-card="handleAddNewCard"
      />
    </template>

    <template v-else-if="step === 'external-kyc'" #body>
      <external-kyc
        :url="redirectUrl"
        @kyc-success="step = 'cards'"
        @kyc-fail="step = 'main'"
      />
    </template>

    <template v-else-if="step === 'external-withdraw-params'" #body>
      <external-withdraw-params
        :url="redirectUrl"
        @mercuryo-sell-success="handleWithdrawToMercuryo"
        @mercuryo-sell-fail="handleWithdrawToMercuryoFail"
      />
    </template>

    <template v-else-if="step === 'confirmation'" #body>
      <confirmation
        :coin-amount="coinAmount"
        :fiat-amount="fiatAmount"
        :coin="selectedCoin"
        :phone-number="phoneNumber"
        :is-loading-rate="isLoadingRate"
        :is-withdraw-in-progress="isWithdrawInProgress"
        :rate="rate"
        :rate-time-to-live="rateTimeToLive"
        :fiat="selectedFiat"
        :payment-method="selectedPaymentMethod"
        :card-title="cardNumber"
        :error-code="errorCode"
        @confirm="handleWithdraw"
        @clear-error="errorCode = null"
      />
    </template>

    <template v-else-if="step === 'noPay'" #body>
      <no-pay @close="closeModal" />
    </template>

    <template v-else-if="step === 'kyc'" #body>
      <kyc-restriction @close="closeModal" />
    </template>

    <template v-else-if="step === 'isSub'" #body>
      <switch-to-main-account-modal
        :coin-id="coinId"
        @open="(operation, options) => emit('open', operation, options)"
        @close="closeModal"
      />
    </template>

    <template v-else-if="step === '2fa'" #body>
      <two-f-a />
    </template>

    <template v-else-if="step === 'success'" #body>
      <success
        :coin-amount="order?.coinAmount"
        :fiat-amount="order?.fiatAmount"
        :coin="order?.coin"
        :fiat="order?.fiat"
        :card-number="order?.cardNumber"
        :phone-number="phoneNumber"
        @close="closeModal"
      />
    </template>

    <template v-else-if="step === 'fail'" #body>
      <fail
        :error-code="errorCode"
        @repeat-withdraw="withdrawAgain"
        @close="closeModal"
      />
    </template>

    <template #banner>
      <p2-p-banner />
    </template>
  </modal-wrapper>
</template>

<script setup>
import {
  ref,
  computed,
  getCurrentInstance,
  defineEmits,
  defineProps,
} from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import * as Sentry from '@sentry/vue'
import { fiats } from '../config/fiats'
import { banks } from '../config/banks'
import { providers } from '../config/providers'
import { paymentMethods } from '../config/payment-methods'
import {
  saveWithdrawToCardParamsToStorage,
  getWithdrawToCardParamsFromStorage,
} from '../storage'
import SwitchToMainAccountModal from './SwitchToMainAccountModal.vue'
import Main from './Main.vue'
import Confirmation from './Confirmation.vue'
import ExternalWithdrawParams from './ExternalWithdrawParams'
import ExternalKyc from './ExternalKyc.vue'
import Success from './Success.vue'
import Fail from './Fail.vue'
import { ModalWrapper } from '@/shared/ui/ModalWrapper'
import { formatNumber } from '@/shared/lib/formatNumber'
import { toFixed } from '@/shared/lib/toFixed'
import { NoPay } from '@/features/NoPay'
import { KycRestriction } from '@/features/KycRestriction'
import TwoFA from '@/features/TwoFA'
import { OperationHeader } from '@/features/OperationHeader'
import { SelectCard } from '@/features/SelectCard'
import P2PBanner from '@/features/P2PBanner/P2PBanner.vue'
import { truncateExtraDecimals } from '@/shared/lib/truncateExtraDecimals'
import { CONFIG } from '@/app/config/config'

const DEFAULT_RATE_TIME_TO_LIVE = 120 // in seconds

const { appContext } = getCurrentInstance()
const apiService = appContext.config.globalProperties.$apiService()

// eslint-disable-next-line vue/no-setup-props-destructure
const { visible } = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close', 'open', 'coinChange'])

/** Operation State */

const modalName = ref('WithdrawToCard') // Unique name of the modal for calling
const step = ref('main') // steps: "noPay", "kyc", "isSub", "2fa", "main", "cards", "external-kyc", "external-withdraw-params", "confirmation", "success", "fail"

const coinId = ref('btc') // String with coin id for the selected source coin
const coinAmount = ref('') // Amount in coin for withdraw
const fiatId = ref('RUB') // String with selected fiat id from the API
const fiatAmount = ref('') // Amount in fiat to get after withdraw
const paymentMethodId = ref('mir') // Selected payment method to withdraw
const cardNumber = ref('') // Card number to withdraw
const bankId = ref(null) // Bank SBP code (used only for SBP method payment)
const phoneNumber = ref('') // Phone number (used only for SBP method payment)
const providerId = ref('xForta') // If we can offer several providers, current selected provider ID
const isCheckedTermsOfUse = ref(false) // Checkbox for Mercuryo

const isFromInputActive = ref(true) // We should know which input ("from" or "to") is used by user and which we should calculate automatically
const debounceTimer = ref(null) // Reference to the current setTimeout instance of the input debounce timer

const cards = ref([]) // Array of cards description for Mercuryo
const redirectUrl = ref(null) // Redirect url for Mercuryo
const merchantTransactionId = ref(null) // Special transaction id for Mercuryo

const order = ref(null) // Static data necessary for fiat-proxy/withdraw API method, we use it in success modal

const errorCode = ref(null)
const isInitialized = ref(false) // Is data initialized (data needed before open the modal)
const isLoadingRate = ref(false) // If we sent a request to API to update rate for withdraw and waiting for the result
const isGettingCardsInProgress = ref(false) // If we are waiting for the cards list for Mercuryo
const isWithdrawInProgress = ref(false) // If we just have sent request to API for withdraw and waiting for the result

const rateTimeToLive = ref(DEFAULT_RATE_TIME_TO_LIVE) // How many seconds we can use the current rate
const rateTimerIntervalId = ref(null) // Reference to the current setTimeout instance for auto update of the withdrawal rate or null
const rateId = ref(0) // ID of the latest rate response that we have got from the API
const rate = ref(null) // Current rate to exchange coin to fiat

const store = useStore()
const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()

/** Coins variables */

const settings = computed(() => store.state.withdraw.settings)
const findCoinLimits = computed(() => store.getters['withdraw/findCoinLimits'])
const coins = computed(() => store.state.coinService.coins)
const coinBalances = computed(() => store.state.coinService.coinBalances)
// Description of the source coin from /coin/config or undefined
const selectedCoin = computed(() => coins.value[coinId.value])

// Coins that user can withdraw to card
const availableCoins = computed(() => {
  const availableCoinsResult = {}
  Object.values(coins.value).forEach((coin) => {
    if (settings.value.find((c) => c.crypto === coin.id?.toUpperCase())) {
      availableCoinsResult[coin.id] = coin
    }
  })
  return availableCoinsResult
})

function isCoinAvailable(coinIdValue) {
  return availableCoins.value[coinIdValue]
}

// Return limits for the selected coin + fiat + payment method or undefined
const activeCoinLimits = computed(() =>
  findCoinLimits.value(
    coinId.value?.toUpperCase(),
    fiatId.value,
    paymentMethodId.value
  )
)

const availableFunds = computed(() => {
  const balance = store.getters['wallets/walletsBalanceByCoin'][coinId.value]
  return +toFixed(balance)
})

const coinAmountMinLimit = computed(() => {
  if (!activeCoinLimits.value || !rate.value) return 0
  const min = activeCoinLimits.value.min / rate.value
  return Math.ceil(min * 10 ** 8) / 10 ** 8
})

const coinAmountMaxLimit = computed(() => {
  if (!activeCoinLimits.value || !rate.value) return availableFunds.value
  const max = activeCoinLimits.value.max / rate.value
  return max >= availableFunds.value
    ? availableFunds.value
    : Math.floor(max * 10 ** 8) / 10 ** 8
})

const isCoinAmountCorrect = computed(
  () =>
    coinAmount.value >= coinAmountMinLimit.value &&
    coinAmount.value <= coinAmountMaxLimit.value
)

// User balance in the selected coin
const selectedCoinBalance = computed(() => {
  const coinBalance = coinBalances.value[coinId.value]?.wallet_balance
  const balance = toFixed(coinBalance)
  return parseFloat(balance)
})

// How to validate withdraw sum in input
// First - check that user input more or equal than min limit for the transaction
// Second - check that user has a balance enough to perform the withdrawal (balance - fee >= min limit)
// Third - check that amount is less or equal than max limit
const coinAmountValidationRules = computed(() => {
  // Don't validate "from" input, because user inputs value to the "to" input
  if (!isFromInputActive.value) return ''

  // Don't validate "from" input if we can't calculate limits based on rate
  if (!rate.value) return ''

  if (coinAmount.value === '' || coinAmount.value === 0) return ''
  return `gte_with_coin:${formatNumber(coinAmountMinLimit.value)},${selectedCoin.value?.title}|balance_limit:${coinAmountMinLimit.value},${coinAmountMaxLimit.value}|lte_with_coin:${coinAmountMaxLimit.value},${selectedCoin.value?.title}`
})

// The number of decimals that should be used in the coin amount field
const allowedCoinAmountDecimals = computed(() => {
  // TODO: remove when API will give us decimals in settings
  if (providerId.value === 'mercuryo' && coinId.value === 'usdt') return 6

  return 8
})

function showErrorModal(error) {
  if (error === 'fiat-0001' || error === 'fiat-0002') {
    step.value = 'fail'
  }
}

/** Fiat variables */

// Fiats that user can to get after withdraw to card
const availableFiats = computed(() => {
  const availableFiatsSet = new Set()
  settings.value.forEach((setting) => {
    // Get only settings with current selected coin
    if (setting.crypto !== coinId.value?.toUpperCase()) return
    availableFiatsSet.add(setting.fiat)
  })

  // Sort by alphabet
  const sortedArray = Array.from(availableFiatsSet).sort((a, b) =>
    a.localeCompare(b)
  )
  // Enrich with additional data from the config
  return sortedArray.map((item) => fiats[item])
})

// Description of the selected target fiat currency
const selectedFiat = computed(() =>
  availableFiats.value.find((fiat) => fiat.value === fiatId.value)
)

const isCurrentFiatAvailable = computed(
  () =>
    availableFiats.value.findIndex((item) => item.value === fiatId.value) !== -1
)

const fiatAmountMinLimit = computed(() => {
  if (!activeCoinLimits.value) return 0
  return activeCoinLimits.value.min
})

const fiatAmountMaxLimit = computed(() => {
  if (!activeCoinLimits.value && !rate.value) return Infinity
  if (!activeCoinLimits.value) return availableFunds.value * rate.value
  const coinBalanceInFiat = availableFunds.value * rate.value
  return Math.min(activeCoinLimits.value.max, coinBalanceInFiat)
})
// The number of decimals that should be used in the fiat amount field
const allowedFiatAmountDecimals = computed(() => 2)

// How to validate target sum in fiat for withdraw
const fiatAmountValidationRules = computed(() => {
  // Don't validate "to" input, because user inputs value to the "from" input
  if (isFromInputActive.value) return ''

  if (fiatAmount.value === '' || fiatAmount.value === 0) return ''
  return `gte_with_coin:${fiatAmountMinLimit.value},${selectedFiat.value?.value}|balance_limit:${fiatAmountMinLimit.value},${fiatAmountMaxLimit.value}|lte_with_coin:${fiatAmountMaxLimit.value},${selectedFiat.value?.value}`
})

/** Get Fiat Rate */

// Calculate and return the number of seconds until next update rate or undefined
function convertToSeconds(dateString) {
  if (!dateString) return undefined

  const specificDate = new Date(dateString)?.getTime()
  if (!specificDate) return undefined

  const now = new Date()
  const distance = specificDate - now
  const seconds = Math.floor(distance / 1000)
  return seconds
}

function clear() {
  coinAmount.value = ''
  fiatId.value = 'RUB'
  fiatAmount.value = ''
  paymentMethodId.value = 'mir'
  cardNumber.value = ''
  bankId.value = null
  phoneNumber.value = ''
  providerId.value = 'xForta'
  isCheckedTermsOfUse.value = false
  order.value = null
  cards.value = []
  errorCode.value = null
  merchantTransactionId.value = null
  isWithdrawInProgress.value = false
}

// Fetch the current fiat rate and update the operation state
async function getFiatRate() {
  // Prepare data for a request
  const rateJustForInformation = coinAmount.value === ''
  const coinAmountForRequest = rateJustForInformation ? '0' : coinAmount.value

  // Make a request
  const res = await store.dispatch('withdraw/FIAT_RATE', {
    amount: coinAmountForRequest,
    crypto: coinId.value?.toUpperCase(),
    fiat: fiatId.value,
    payment_type: paymentMethodId.value,
    provider: providerId.value,
    public: !isCheckedTermsOfUse.value,
  })

  if (!res) return

  // Handle error and return
  if (res?.code) {
    showErrorModal(res?.code)
    errorCode.value = res?.code
    rateId.value = null
    rate.value = null
    return
  }

  // If success – update rate
  rateId.value = res?.id
  rate.value = +(res?.rate ?? 0)
  rateTimeToLive.value =
    convertToSeconds(res?.valid_till) ?? DEFAULT_RATE_TIME_TO_LIVE

  // If success and user didn't change coin amount - update amount in fiat
  if (!rateJustForInformation && coinAmount.value === coinAmountForRequest) {
    fiatAmount.value = truncateExtraDecimals(
      res?.value,
      allowedFiatAmountDecimals.value
    )
    return
  }

  // If success and user didn't input anything in coin amount input - clear fiat amount
  if (rateJustForInformation && coinAmount.value === '') {
    fiatAmount.value = ''
  }
}

// Stop current timer for update withdraw rate
function stopTimer() {
  if (debounceTimer.value) clearTimeout(debounceTimer.value)
  debounceTimer.value = null
  if (rateTimerIntervalId.value) clearTimeout(rateTimerIntervalId.value)
  rateTimerIntervalId.value = null
}

// Auto update the rate
async function updateRateByTimer() {
  isLoadingRate.value = true

  stopTimer()
  await getFiatRate()
  rateTimerIntervalId.value = setTimeout(
    updateRateByTimer.value,
    rateTimeToLive.value * 1000
  )

  isLoadingRate.value = false
}

/** Payment methods variables */

// Payment methods that user can choose from
const availablePaymentMethods = computed(() => {
  const availablePaymentMethodsResult = new Set()

  settings.value.forEach((setting) => {
    // Get only settings with current selected coin
    if (setting.crypto !== coinId.value?.toUpperCase()) return
    // Get only settings with current selected fiat
    if (setting.fiat !== fiatId.value) return
    availablePaymentMethodsResult.add(setting.payment_type)
  })

  // Sort by alphabet
  const sortedArray = Array.from(availablePaymentMethodsResult).sort((a, b) =>
    a.localeCompare(b)
  )

  // Enrich with additional data from the config
  return sortedArray.map((item) => paymentMethods[item])
})

const selectedPaymentMethod = computed(() =>
  availablePaymentMethods.value.find(
    (method) => method.value === paymentMethodId.value
  )
)

// Available for the user banks if user would like to withdraw throw SBP
const availableBanks = computed(() => banks)

// Available for the user providers to withdraw throw
const availableProviders = computed(() => {
  const availableProvidersResult = new Set()
  settings.value.forEach((setting) => {
    // Get only settings with current selected coin
    if (setting.crypto !== coinId.value?.toUpperCase()) return false
    // Get only settings with current selected fiat
    if (setting.fiat !== fiatId.value) return false
    // Get only settings with current selected payment type
    if (setting.payment_type !== paymentMethodId.value) return false
    availableProvidersResult.add(setting.provider)
    return true
  })

  // Sort by alphabet
  const sortedArray = Array.from(availableProvidersResult).sort((a, b) =>
    a.localeCompare(b)
  )

  // Enrich with additional data from the config
  return sortedArray.map((item) => providers[item])
})

const isCurrentPaymentMethodAvailable = computed(
  () =>
    availablePaymentMethods.value.findIndex(
      (method) => method.value === paymentMethodId.value
    ) !== -1
)

const isCurrentProviderAvailable = computed(
  () =>
    availableProviders.value.findIndex(
      (provider) => provider.value === providerId.value
    ) !== -1
)

// Check correctness of the card number
const isCardNumberCorrect = computed(() => {
  if (!selectedPaymentMethod.value?.isCardNumberUsed) return true
  if (providerId.value === 'mercuryo') return true
  return cardNumber.value.length === 16
})

const isPhoneNumberCorrect = computed(() => {
  if (!selectedPaymentMethod.value?.isPhoneNumberUsed) return true
  const number = phoneNumber.value
  return number.replace(/\D/g, '').length === 11
})

const isBankIdCorrect = computed(() => {
  if (!selectedPaymentMethod.value?.isPhoneNumberUsed) return true
  return !!bankId.value
})

// We should have checked terms of use only for Mercuryo
const isCheckedTermsOfUseCorrect = computed(() =>
  providerId.value === 'mercuryo' ? isCheckedTermsOfUse.value : true
)

// Is it possible to make an withdraw according to frontend requirements?
const isWithdrawAvailable = computed(
  () =>
    isCoinAmountCorrect.value &&
    isCardNumberCorrect.value &&
    isPhoneNumberCorrect.value &&
    isCheckedTermsOfUseCorrect.value &&
    isBankIdCorrect.value &&
    isInitialized.value &&
    !isLoadingRate.value &&
    !isGettingCardsInProgress.value &&
    !isWithdrawInProgress.value &&
    !errorCode.value
)

/** Event handlers */

async function changeCoinUrl(value) {
  const newQuery = { ...route.query, coin: value || coinId.value }
  await router.replace({ query: newQuery })
}

// When user changed coin
async function handleChangeCoin(value) {
  coinId.value = value

  // Change the query if the coin is changed
  if (route.query.coin !== value) {
    await changeCoinUrl(value)
  }

  // Clear all the form
  clear()

  // Set form fields to valid values
  if (!isCurrentFiatAvailable.value) {
    fiatId.value = availableFiats.value[0]?.value
  }
  if (!isCurrentPaymentMethodAvailable.value) {
    paymentMethodId.value = availablePaymentMethods.value[0]?.value
  }
  if (!isCurrentProviderAvailable.value) {
    providerId.value = availableProviders.value[0]?.value
  }

  // Update rate for converting a new coin to fiat
  await updateRateByTimer()
}

// When user changed amount of the coin to withdraw
function handleChangeCoinAmount(value) {
  coinAmount.value = truncateExtraDecimals(
    value,
    allowedCoinAmountDecimals.value
  )
  isFromInputActive.value = true

  // Refresh rate if user changed the amount
  stopTimer()
  debounceTimer.value = setTimeout(() => {
    updateRateByTimer()
  }, 1000)
}

// When user changed fiat currency
async function handleChangeFiat(value) {
  fiatId.value = value

  // Set form fields to valid values
  if (!isCurrentPaymentMethodAvailable.value) {
    paymentMethodId.value = availablePaymentMethods.value[0]?.value
  }
  if (!isCurrentProviderAvailable.value) {
    providerId.value = availableProviders.value[0]?.value
  }

  // Properly truncate coin amount based on the new fiat requirements
  coinAmount.value = truncateExtraDecimals(
    coinAmount.value,
    allowedCoinAmountDecimals.value
  )

  // Update rate for converting a new coin to fiat
  await updateRateByTimer()
}

// When user changed amount of the fiat to get after withdraw
function handleChangeFiatAmount(value) {
  fiatAmount.value = truncateExtraDecimals(
    value,
    allowedFiatAmountDecimals.value
  )
  isFromInputActive.value = false

  if (!rate.value) return
  coinAmount.value = truncateExtraDecimals(
    value / rate.value,
    allowedCoinAmountDecimals.value
  )

  // Refresh rate if user changed the amount
  stopTimer()
  debounceTimer.value = setTimeout(() => {
    updateRateByTimer()
  }, 1000)
}

// When user clicks on the chip
function handleChipClick(value) {
  handleChangeFiatAmount(value)
}

// When user changed payment method
async function handleChangePaymentMethod(value) {
  paymentMethodId.value = value

  // Set form fields to valid values
  if (!isCurrentProviderAvailable.value) {
    providerId.value = availableProviders.value[0]?.value
  }

  // Update rate for converting a new coin to fiat
  await updateRateByTimer()
}

// When user change card number
function handleChangeCardNumber(value) {
  cardNumber.value = value
}

// When user select another bank from the list
function handleBankSelection(value) {
  bankId.value = value
}

// When user select another provider for withdraw
async function handleChangeProvider(value) {
  providerId.value = value

  // Update rate for converting a new coin to fiat
  await updateRateByTimer()
}

function handleChangePhoneNumber(value) {
  phoneNumber.value = value
}

function handleCheckTermsOfUse(value) {
  isCheckedTermsOfUse.value = value
}

function handleInputFocus(value) {
  isFromInputActive.value = value === 'from'
}

// If user would like to go back from the confirmation / cards modal
async function backToMainStep() {
  await updateRateByTimer()
  step.value = 'main'
}

// When user select one of the card for Mercuryo
async function handleSelectCard(cardId) {
  const {
    redirectUrl: bindRedirectUrl,
    merchantTransactionId: merchantTransactionIdResponse,
    error: bindError,
  } = await store.dispatch('withdraw/FIAT_BIND_CARD', { cardId })

  // If external KYC is needed
  if (bindError?.code === 'fiat-proxy-1001') {
    const { redirectUrl: kycRedirectUrl, error: kycError } =
      await store.dispatch('withdraw/FIAT_EXTERNAL_KYC')
    if (kycError) {
      errorCode.value = kycError?.code
      showErrorModal(errorCode.value)
      return
    }

    redirectUrl.value = kycRedirectUrl
    step.value = 'external-kyc'
    return
  }

  // If faced with error
  if (bindError) {
    errorCode.value = bindError?.code
    showErrorModal(errorCode.value)
    return
  }

  redirectUrl.value = bindRedirectUrl
  merchantTransactionId.value = merchantTransactionIdResponse
  step.value = 'external-withdraw-params'
}

// When user wants to add a new card for Mercuryo
async function handleAddNewCard() {
  await handleSelectCard('')
}

// Used only for Mercuryo for authorize user and get his cards
async function getCards() {
  isGettingCardsInProgress.value = true

  // Authenticate user based on his emcd email
  const { error: authError } = await store.dispatch('withdraw/FIAT_AUTH')
  if (authError) {
    errorCode.value = authError?.code
    showErrorModal(errorCode.value)
    isGettingCardsInProgress.value = false
    return
  }

  // Get and show cards
  const { error: cardsError, cards: cardsResponse } = await store.dispatch(
    'withdraw/FIAT_GET_CARDS'
  )

  if (cardsError) {
    errorCode.value = cardsError?.code
    showErrorModal(errorCode.value)
  } else {
    errorCode.value = null
    cards.value = cardsResponse
  }

  isGettingCardsInProgress.value = false
}

async function onSubmit() {
  // Special case for Mercuryo
  if (providerId.value === 'mercuryo') {
    await getCards()
    if (errorCode.value === null) step.value = 'cards'
    await updateRateByTimer()
    return
  }

  // Common case
  await updateRateByTimer()
  step.value = 'confirmation'
}

// Send data about withdraw to analytics
function sendWithdrawAnalytics() {
  window.emcd.AnalyticsService.track('Click - Web', {
    lang: locale.value,
    page: 'Withdraw - Popup',
    click: 'Confirm withdrawal button',
    submit_success: true,
    coin: coinId.value.toLowerCase(),
  })
}

// Save fixed withdraw parameters for success window
function fixOrderInformation() {
  order.value = {
    coinAmount: coinAmount.value,
    fiatAmount: fiatAmount.value,
    coin: selectedCoin.value,
    fiat: selectedFiat.value,
    cardNumber: cardNumber.value,
  }
}

// Specific withdraw logic for Mercuryo
async function handleWithdrawToMercuryo({ address, signature }) {
  isWithdrawInProgress.value = true
  fixOrderInformation()

  // Prepare parameters for API call
  const orderRequest = {
    amount: String(coinAmount.value),
    bank: bankId.value,
    bankSbpCode: bankId.value,
    card: String(cardNumber.value),
    crypto: coinId.value?.toUpperCase(),
    payment_type: paymentMethodId.value,
    phone: String(phoneNumber.value),
    rate_id: rateId.value,
    merchant_transaction_id: merchantTransactionId.value,
    signature,
    wallet_address: address,
  }

  const { success, response } = await store.dispatch(
    'withdraw/FIAT_WITHDRAW',
    orderRequest
  )
  if (success) {
    errorCode.value = null
    step.value = 'success'

    // Update balances
    setTimeout(() => {
      store.dispatch('coinService/GET_BALANCES', apiService)
    }, 4000)
  } else {
    errorCode.value = response?.code
    order.value = null
    step.value = 'fail'
  }

  isWithdrawInProgress.value = false
  sendWithdrawAnalytics()
}

// Send a withdrawal order to the API
async function handleWithdraw() {
  isWithdrawInProgress.value = true
  fixOrderInformation()

  let phoneNumberValue = phoneNumber.value
  phoneNumberValue = phoneNumberValue.replace(/\D/g, '')
  const firstChar = phoneNumberValue.charAt(0)
  if (firstChar === '8') {
    phoneNumberValue = `7${phoneNumberValue.slice(1)}`
  }

  // Prepare parameters for API call
  // TODO: костыль, если платежная система "mir", то бек ждет строку "Все банки РФ" в поле bank
  const orderRequest = {
    amount: String(coinAmount.value),
    bank: bankId.value ?? 'Все банки РФ',
    bankSbpCode: bankId.value ?? '',
    card: String(cardNumber.value),
    crypto: coinId.value?.toUpperCase(),
    payment_type: paymentMethodId.value,
    phone: String(phoneNumberValue),
    rate_id: rateId.value,
    merchant_transaction_id: '',
    signature: '',
    wallet_address: '',
  }

  const { success, response } = await store.dispatch(
    'withdraw/FIAT_WITHDRAW',
    orderRequest
  )
  if (success) {
    errorCode.value = null
    step.value = 'success'

    saveWithdrawToCardParamsToStorage({
      coinId: coinId.value,
      fiatId: fiatId.value,
      providerId: providerId.value,
      paymentMethodId: paymentMethodId.value,
    })

    // Update balances
    setTimeout(() => {
      store.dispatch('coinService/GET_BALANCES', apiService)
    }, 4000)
  } else {
    errorCode.value = response?.code
    order.value = null
    step.value = 'fail'
  }

  isWithdrawInProgress.value = false
  sendWithdrawAnalytics()
}

// If Mercuryo redirect us to fail url when tried to bind a card
function handleWithdrawToMercuryoFail() {
  errorCode.value = 'fiat-0001'
  order.value = null
  showErrorModal('fiat-0001')

  // Sent error of withdraw through Mercuryo to Sentry
  // TODO: in the future it's enough to send only real errors
  const error = new Error()
  error.name = `Mercuryo: withdraw failed`
  error.message = JSON.stringify({
    coinId: coinId.value,
    coinAmount: coinAmount.value,
    fiatId: fiatId.value,
    fiatAmount: fiatAmount.value,
    paymentMethodId: paymentMethodId.value,
    providerId: providerId.value,
    isCheckedTermsOfUse: isCheckedTermsOfUse.value,
    redirectUrl: redirectUrl.value,
    merchantTransactionId: merchantTransactionId.value,
    errorCode: errorCode.value,
    rate: rate.value,
    rateId: rateId.value,
  })
  Sentry.captureException(error)
}

async function setInitialWithdrawParams() {
  // Get coin id from url params if it contains correct coin
  const coinIdFromUrlParams = route.query.coin?.toLowerCase()
  // Get previous successful withdraw data
  const previousWithdrawal = getWithdrawToCardParamsFromStorage()
  if (isCoinAvailable(coinIdFromUrlParams)) {
    coinId.value = coinIdFromUrlParams
  } else if (
    previousWithdrawal &&
    isCoinAvailable(previousWithdrawal?.coinId)
  ) {
    coinId.value = previousWithdrawal?.coinId
    providerId.value = previousWithdrawal?.providerId
    paymentMethodId.value = previousWithdrawal?.paymentMethodId
    fiatId.value = previousWithdrawal?.fiatId
  } else {
    const isBtcInSettings = !!settings.value.find(
      (setting) => setting.crypto === 'BTC'
    )
    coinId.value = isBtcInSettings
      ? 'btc'
      : Object.keys(availableCoins.value)[0]
  }

  // Set form fields to valid values
  if (!isCurrentFiatAvailable.value) {
    fiatId.value =
      availableFiats.value.length > 0 ? availableFiats.value[0]?.value : 'RUB'
  }
  if (!isCurrentPaymentMethodAvailable.value) {
    paymentMethodId.value =
      availablePaymentMethods.value.length > 0
        ? availablePaymentMethods.value[0]?.value
        : 'mir'
  }
  if (!isCurrentProviderAvailable.value) {
    providerId.value =
      availableProviders.value.length > 0
        ? availableProviders.value[0]?.value
        : 'xForta'
  }
}

// Method to repeat withdraw if user has got a fail
async function withdrawAgain() {
  // Get all available coins for withdraw to fiat and all the params
  const result = await store.dispatch('withdraw/FIAT_SETTINGS')
  await setInitialWithdrawParams()
  await updateRateByTimer()

  if (result.length > 0) {
    clear()
    step.value = 'main'
  } else if (result?.code) {
    errorCode.value = result?.code
  }
}

/** Open / Close widget */

const webWalletsKycRestrictions = computed(
  () => store.state.ui.webWalletsKycRestrictions
)

const isSub = computed(() => store.getters['auth/isSub'])

async function beforeOpen() {
  isInitialized.value = false

  // Check no pay for the user
  const noPay = await store.dispatch('user/GET_NO_PAY')
  if (noPay) {
    step.value = 'noPay'
    return
  }

  // Check KYC
  if (webWalletsKycRestrictions.value) {
    const isKycPassed = await store.dispatch('user/GET_KYC_STATUS')
    if (!isKycPassed) {
      step.value = 'kyc'
      return
    }
  }

  // Check that user try to withdraw from the main account, not sub account
  if (isSub.value) {
    step.value = 'isSub'
    return
  }

  // Check 2FA is enabled for the user
  const twoFa = await store.dispatch('user/GET_2FA')
  if (!twoFa) {
    step.value = '2fa'
    return
  }

  // Clear form
  clear()

  // Get all available coins for withdraw to fiat and all the params
  const result = await store.dispatch('withdraw/FIAT_SETTINGS')

  if (result?.code) {
    errorCode.value = result?.code
    showErrorModal(result.code)
  }

  await setInitialWithdrawParams()

  // Change the query if the coin is changed
  if (route.query.coin !== coinId.value) {
    await changeCoinUrl()
  }

  // Get rate for conversion coin to fiat
  await updateRateByTimer()

  isInitialized.value = true
}

function beforeClose() {
  clear()
  stopTimer()
  coinId.value = 'btc'
  step.value = 'main'
}

// Helper to properly close this modal
function closeModal() {
  emit('close')
}

// Prepare alternative options for the header popup
const headerPopupItems = computed(() => {
  const p2p = {
    iconName: 'SwapHorizontal',
    title: t('WithdrawToCard | headerPopoverP2PTitle'),
    desc: t('WithdrawToCard | headerPopoverP2PDesc'),
    action: () => {
      window.location.href = `${CONFIG.EMCD_URL}/p2p`
    },
  }

  const withdrawToWallet = {
    iconName: 'qr-code-withdraw',
    title: t('WithdrawToCard | headerPopoverWithdrawToCardTitle'),
    desc: t('WithdrawToCard | headerPopoverWithdrawToCardDesc'),
    action: () => {
      emit('open', 'WithdrawToWallet', {
        coin: coinId.value,
      })
    },
  }

  return [p2p, withdrawToWallet]
})
</script>
